import { useState } from 'react';
import { X, Copy, ExternalLink, Check } from 'lucide-react';

export default function ContactModal({ type, onClose }) {
  const [copied, setCopied] = useState(false);

  const isWhatsApp = type === 'whatsapp';
  const isEmail = type === 'email';
  const isPhone = type === 'phone';

  const config = {
    whatsapp: {
      title: 'Chat on WhatsApp',
      value: '+91 98430 35014',
      openLabel: 'Open WhatsApp',
      openUrl: 'https://wa.me/919843035014?text=Hi%20Apex%20Precitech%2C%20I%20would%20like%20to%20get%20a%20quote.',
      color: 'bg-green-600 hover:bg-green-700',
    },
    email: {
      title: 'Send us an Email',
      value: 'apexpreci@gmail.com',
      openLabel: 'Open Mail App',
      openUrl: 'mailto:apexpreci@gmail.com',
      color: 'bg-steel-800 hover:bg-steel-900',
    },
    phone: {
      title: 'Call Us',
      value: '+91 98430 35014',
      openLabel: 'Call Now',
      openUrl: 'tel:+919843035014',
      color: 'bg-blue-600 hover:bg-blue-700',
    },
  };

  const c = config[type] || config.phone;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(c.value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.createElement('textarea');
      el.value = c.value;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div
      data-testid="contact-modal-overlay"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4"
      onClick={onClose}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-steel-900/60 backdrop-blur-sm" />

      {/* Modal */}
      <div
        data-testid="contact-modal"
        className="relative bg-white border-2 border-steel-900 shadow-[6px_6px_0px_0px_rgba(15,23,42,1)] w-full max-w-md p-0 animate-fade-in-up"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b-2 border-steel-900 px-6 py-4">
          <h3 className="font-heading text-xl uppercase tracking-wide text-steel-900">{c.title}</h3>
          <button
            data-testid="contact-modal-close"
            onClick={onClose}
            className="p-1 hover:bg-steel-100 transition-colors"
          >
            <X size={20} className="text-steel-600" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-8">
          <div className="bg-steel-50 border border-steel-200 px-6 py-4 text-center mb-6">
            <p className="font-mono text-xl md:text-2xl font-semibold text-steel-900 tracking-wide select-all">
              {c.value}
            </p>
          </div>

          <div className="flex gap-3">
            <button
              data-testid="contact-modal-copy"
              onClick={handleCopy}
              className={`flex-1 flex items-center justify-center gap-2 border-2 border-steel-900 py-3 px-4 font-heading uppercase tracking-wider text-sm transition-all duration-200 ${
                copied
                  ? 'bg-green-50 text-green-700 border-green-600'
                  : 'bg-white text-steel-900 hover:bg-steel-50'
              }`}
            >
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? 'Copied!' : `Copy ${isEmail ? 'Email' : 'Number'}`}
            </button>

            <a
              data-testid="contact-modal-open"
              href={c.openUrl}
              target={isWhatsApp ? '_blank' : '_self'}
              rel="noopener noreferrer"
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 text-white font-heading uppercase tracking-wider text-sm transition-all duration-200 hover:-translate-y-0.5 ${c.color}`}
            >
              <ExternalLink size={16} />
              {c.openLabel}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
